import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, zip } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { Firm } from '../entity/firm';
import { FirmSettings } from '../entity/firm.settings';
import { FirmSelectors } from '../state/firm/firm.selectors';
import { toFirmTransformer } from '../state/transformers/transformers';
import { ActivateSustainabilityReporting, FirmType } from '../state/types';
import { BLService } from './bl.service';

@Injectable({
  providedIn: 'root',
})
export class FirmService extends BLService {
  public firmAssignmentData$: Observable<FirmSettings>;
  public assignmentAgreementFirmData$: Observable<[Firm, FirmSettings]>;
  private RESOURCE_ENDPOINT = 'firm';
  private firmAssignmentDataBS = new BehaviorSubject(new FirmSettings());

  constructor() {
    super();

    this.firmAssignmentData$ = this.firmAssignmentDataBS.asObservable();
    this.assignmentAgreementFirmData$ = zip(
      this.store.select(FirmSelectors.selectFirm).pipe(map(toFirmTransformer.transform)),
      this.firmAssignmentData$,
    );
  }

  loadFirmSettings(): Observable<FirmSettings> {
    return zip(this.getFirmSettings(), this.getMaxLengthForAssignmentValues()).pipe(
      map(([firmSettings, maxLengthRes]) => ({ ...firmSettings, maxLength: maxLengthRes.maxLength })),
    );
  }

  loadFirmAssignmentData() {
    return this.loadFirmSettings().subscribe((res) => {
      this.firmAssignmentDataBS.next(res);
    });
  }

  loadFirmType(): Observable<FirmType> {
    return this.httpGet<FirmType>(`${this.RESOURCE_ENDPOINT}/current`);
  }

  setFirmAssignmentData(val: FirmSettings): void {
    this.firmAssignmentDataBS.next(val);
  }

  updateFirmType(firm: FirmType): Observable<FirmType> {
    return this.httpPut<FirmType>(this.RESOURCE_ENDPOINT, firm);
  }

  updateFirmSettings(firmSettings: FirmSettings): Observable<FirmSettings> {
    return this.httpPut<FirmSettings>(`${this.RESOURCE_ENDPOINT}/settings`, firmSettings);
  }

  updateFirmBlAgentStatus(state: string) {
    return this.httpPut(`${this.RESOURCE_ENDPOINT}/settings/use_old_bl_agent/${state}`, '');
  }

  resetFirmSettings(): Observable<FirmSettings> {
    return this.httpDelete<FirmSettings>(`${this.RESOURCE_ENDPOINT}/settings`);
  }

  resetFirmTasks(firmId: number): Observable<{}> {
    return this.httpDelete<{}>(`${this.RESOURCE_ENDPOINT}/${firmId}/tasks`);
  }

  resetFirm(firmId: number): Observable<{}> {
    return this.httpDelete<{}>(`${this.RESOURCE_ENDPOINT}/${firmId}`);
  }

  getKeyFromBackend(body: any): Observable<any> {
    return this.httpPost<any>(
      'information-logistics/values',
      body,
      this.httpAuthHeader.getHeaderWithResponseType('text'),
    ).pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  activateSustainabilityReporting(firmId: number, body: ActivateSustainabilityReporting): Observable<void> {
    return this.httpPost<void>(
      `${this.RESOURCE_ENDPOINT}/${firmId}/sustainability-reporting`,
      body,
      this.httpAuthHeader.getHeaderWithResponseType('text'),
    );
  }

  private getFirmSettings(): Observable<FirmSettings> {
    return this.httpGet<FirmSettings>(`${this.RESOURCE_ENDPOINT}/settings`);
  }

  private getMaxLengthForAssignmentValues(): Observable<{ maxLength: number }> {
    return this.httpGet<{ maxLength: number }>(`${this.RESOURCE_ENDPOINT}/settings/max-length`);
  }
}
